import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import API_URL from "../config/api";
import { setSession, clearSession } from "./action";
import "./stylePr.css";

// Where each role belongs when it lands somewhere it is not allowed to be.
const HOME_FOR_ROLE = {
  admin: "/dashboard/mainBody",
  agent: "/dashboardAgent/MainBodyAgent",
  user: "/",
};

/**
 * Restores the session from the server rather than from document.cookie.
 *
 * The auth cookies are httpOnly, so JavaScript cannot see them - the only way
 * to know whether a session is live is to ask the API. On a 401 we attempt a
 * single token refresh (the refresh cookie may still be valid even though the
 * access cookie expired) and retry once before giving up.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const dispatch = useDispatch();
  const { role, isAuthenticated } = useSelector((state) => state);
  const [checked, setChecked] = useState(isAuthenticated !== null);

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/users/me`);
        if (!cancelled) dispatch(setSession(data));
      } catch (error) {
        if (error.response?.status !== 401) {
          if (!cancelled) dispatch(clearSession());
          return;
        }

        // Access cookie expired - try to mint a new one from the refresh cookie.
        try {
          await axios.post(`${API_URL}/users/refresh-token`);
          const { data } = await axios.get(`${API_URL}/users/me`);
          if (!cancelled) dispatch(setSession(data));
        } catch (refreshError) {
          if (!cancelled) dispatch(clearSession());
        }
      } finally {
        if (!cancelled) setChecked(true);
      }
    };

    loadSession();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!checked) {
    return (
      <div className="bodyProtect">
        <p className="loadProtect"></p>
        <div className="spinnerP">
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
          <div />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role gate: a signed-in user who does not hold one of the roles this route
  // is for is sent to their own landing page instead of being shown the route.
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={HOME_FOR_ROLE[role] || "/"} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
